/**
 * 「AI をつなぐ」の言葉の正本（1 か所）。2026-10-11 console/one-connect で作り直した。
 *
 * founder 2026-10-11:「ダッシュボードのプロンプトって面倒くさいから、ひとまとめでいいんじゃないの？ あとはエージェントがいい感じでやってくれるでしょ。
 *   人間はコピペするだけでいい。なんならドキュメントの URL を貼るだけで、エージェントが勝手にやってほしい」
 *
 * 作りは 2 段:
 *   1. oneConnectPrompt … 人がコピーして、どの AI（Claude Code・Codex・Cursor・Web やスマホの Claude・ChatGPT）にも貼る短い文。
 *      中身は「案内のページの URL を読んで、自分に合う部分に従って」＋読めないときの最低限の事実（MCP の URL・connect のコマンド）だけ。
 *      どの AI かを人に選ばせない（以前は「Where do you use AI?」の 2 つのボタン = 選ぶ手間と、選び間違いがあった）。
 *   2. 案内のページ（content/<lang>/connect.md = サイトの /docs/connect/ と、AI が読みやすい /docs/connect/index.md）… AI に向けた指示。
 *      まず自分がどれか判断させ、コマンドを実行できるエージェントは connect の道、Web・スマホの Claude / ChatGPT は下の webGuide の道。
 *      webGuide の文は本文の `{{connectWebGuide}}` を build.mjs の substitute が置き換える（文をここ 1 か所に置くため）。
 *
 * 使う所:
 *   - ダッシュボード（apps/dashboard/src/webConnect.ts → ホームの「つなぐ」の段・/guide）… oneConnectPrompt を直接 import する。
 *     /guide は Preview が開いているときだけ previewConnectPrompt（2026-10-16 console/guide-preview）。ホームの「つなぐ」の段も同じ（console/home-preview・ConnectBody が決める）
 *   - サイトの案内のページ /docs/connect/ … webGuide
 *   - サイトの手順のページ /docs/claude-and-chatgpt/ の「貼る文」… oneConnectPrompt（ダッシュボードと同じ 1 つの文。人が読む絵つきの手順はそのページに残す）。
 *     Preview のビルド（SITE_PREVIEW_MCP_URL・2026-10-16 site/all-preview）は previewConnectPrompt（build.mjs sitePrompt）
 * WHY 1 か所か: Claude・ChatGPT の画面の押す場所は変わる。文を二重に持つと片方だけ古くなり、そちらから貼った人だけ古い場所を案内される。
 *
 * ファイルの決まり:
 *   - import が何も無い = Vite（ダッシュボード）と node（サイトのビルド・テスト）のどちらからでも読める。型は隣の connectPrompt.d.mts
 *   - URL は呼ぶ側の設定から（サイト = SITE_URL・SITE_MCP_URL・SITE_API_URL・SITE_DASHBOARD_URL、ダッシュボード = VITE_KUMO_SITE_URL と
 *     /v1/platform/info）= ドメインを直書きしない
 *   - 文を変えたら、サイトの connectPrompt.test.mjs とダッシュボードの webConnect.test.ts の両方を回す
 *
 * webGuide の決まりの経緯（2026-10-11 console/web-connect・founder の Claude / ChatGPT での試し。以前は Web に貼る長いプロンプトとして持っていた）:
 *   - 何も質問させない（「どのアプリか・プランは？」を聞かせたら「結構無駄な作業を言ってくる」）。例外（Free の制限・管理者・開発者モード）は画面が違うときだけ
 *   - 1 ステップずつ「done」を待たせる（一度に全部書くと、スマホの人は手順を見失う）
 *   - リンクは新しいタブ（ChatGPT で「チャットしながら設定画面を開けない。どっちかの画面になっちゃう」）
 *   - 設定画面の URL はこちらで書かない（「変わる可能性があるから、URL は都度エージェントに書かせればいい」）。リンクで出せという決まりだけ
 *   - 初めての設定として案内させる（「殆どの人は最初のセッティングだから KUMO があるわけない」）。もう道具が使える AI は 1 行で伝えて先へ
 *   - 押す場所の事実は残す（削ると AI が推測で埋める）。画面のボタン名の正は docs/research/2026-10-11-web-connect-ui.md
 *   - タブや画面を操作できる AI は設定画面の手順を全部自分でやる。人にはパスワードと許可のボタンだけ（2026-10-17 console/ai-does-connect）
 *   2026-10-25 console/connect-checklist（founder 10/25・ChatGPT の Web で試して「1 つずつ聞き返す」「拡張機能を入れろ」で面倒だった）で変えた:
 *   - 1 ステップずつ「done」を待たせる → 手順は 1 通にまとめて全部渡す。人は最後までやってから「できた」と戻る（checklistPrompt の WHY）
 *   - 画面を操作できる AI は全部自分で → 拡張機能・画面の操作を求めない。設定は人がする
 *   - 初回と「前に足した（再接続）」を分ける・2 つ目を足さない・切断しない・projects_list が通るまで「つながった」と言わない・
 *     「認証を受け付けました。再試行してください」が 2 回なら止めて文・時刻・段を言う
 *   変えるときは手順のページ（content/<lang>/claude-and-chatgpt.md）と画面の絵（screens.mjs）も一緒に直す。
 */

/** 案内のページの slug（nav.mjs の 1 行・content/<lang>/connect.md）。URL の /docs/connect/ を変えると、配った貼る文が古いページを指す = 変えない */
export const CONNECT_GUIDE_SLUG = 'connect';

/**
 * 案内のページの AI 向けの版（.md）の、サイトの起点からの道（先頭の / なし）。呼ぶ側が自分のサイトの URL を前に付ける。
 * WHY .md か: AI が読むページなので、HTML の枠（横の一覧・フッター）の無い本文だけを読ませる（build.mjs が各ページの index.md を出す）。
 * ja は /ja/ の下（日本語を隠しているビルドでは、/ja/… は英語の同じページへ 301 = 壊れない。build.mjs の _redirects）
 */
export function connectGuidePath(lang) {
  return `${lang === 'ja' ? 'ja/' : ''}docs/${CONNECT_GUIDE_SLUG}/index.md`;
}

/** 人が読む手順のページ（Web の Claude・ChatGPT でつなぐ・絵つき）の slug（nav.mjs・content/<lang>/claude-and-chatgpt.md）。変えると配った貼る文が古いページを指す = 変えない */
export const WEB_STEPS_SLUG = 'claude-and-chatgpt';

/**
 * 手順のページの AI 向けの版（.md）の、サイトの起点からの道（先頭の / なし）。connectGuidePath と同じ形（WHY .md かもそちら）。
 * Preview の貼る文（previewConnectPrompt の stepsUrl）が使う（2026-10-17 console/ai-does-connect）
 */
export function webStepsPath(lang) {
  return `${lang === 'ja' ? 'ja/' : ''}docs/${WEB_STEPS_SLUG}/index.md`;
}

/**
 * Muse（Meta の AI エージェント・https://muse.ai/）のカスタムコネクターの「クライアント ID」の欄に入れる値（2026-10-24 console/muse-client-id）。
 * 正本は MCP の apps/mcp/src/auth/presetClients.ts MUSE_CLIENT_ID（前もって登録した公開クライアント・戻り先は Muse の受け口だけ・シークレットなし
 *   = 鍵ではなく公開してよい）。サイト・ダッシュボードはほかのアプリのコードを import しないので写して持つ（connectPrompt.test.mjs が突き合わせる）。
 * WHY: Muse の画面はクライアント ID が空だと「追加」を押せない。案内が DCR（自動の登録）の前提だけだったので、founder 10/24 は Muse の AI が
 *   自分で /register を呼んで ID をチャットで渡す力技になった（「さいしょからクライアントIDを出しててほしかったですね。ここでつまずくユーザーが多そうです」）
 */
export const MUSE_CLIENT_ID = 'kumodeck-muse';

/**
 * クライアント ID を求めるアプリ（Muse など・ホスト型の OAuth のカスタムコネクター）への 1 行（2026-10-24 console/muse-client-id）。
 *   museClientId … 案内に出す Muse の ID。null = まだその MCP に入っていない（本番の公開の MCP は審査中の固定・site.config.mjs museClientId の WHY）
 *     → そのときは「自分で登録して」だけ（DCR はどの MCP でも動く = 外れた ID を案内しない）
 * 見つけ方（/.well-known/oauth-protected-resource）を書く WHY: 公式の道を書いておけば、エージェントが推測で /register を探さない（founder の困った所 2）
 */
function clientIdLine(ja, museClientId) {
  if (museClientId) {
    return ja
      ? `アプリが「クライアント ID」を求めたら: Muse は \`${museClientId}\`（シークレットなし）。ほかのアプリは、MCP サーバーの /.well-known/oauth-protected-resource から登録の場所を見つけて自分で登録し、その ID を使ってください。`
      : `If the app asks for a client ID: in Muse, use \`${museClientId}\` (no client secret). In another app, find the registration endpoint from the MCP server's /.well-known/oauth-protected-resource, register yourself and use that ID.`;
  }
  return ja
    ? `アプリが「クライアント ID」を求めたら: MCP サーバーの /.well-known/oauth-protected-resource から登録の場所を見つけて自分で登録し、その ID を使ってください（シークレットなし）。`
    : `If the app asks for a client ID: find the registration endpoint from the MCP server's /.well-known/oauth-protected-resource, register yourself and use that ID (no client secret).`;
}

/**
 * つなぐ文の形（2026-10-25 console/connect-checklist・founder 10/25「おｋ。これでいこう」）。oneConnectPrompt と previewConnectPrompt の両方がこれで組む。
 * 正は founder が OK した ChatGPT 用の文 docs/teams/console/connect/2026-10-25-chatgpt-checklist-prompt.txt（中身・順番・言い方）。
 *   きっかけ: ChatGPT の Web で以前の文を試したら「1 つずつ聞き返す」「拡張機能を入れろ」で面倒だった。
 *   → 拡張機能・画面の操作を求めない / 手順は 1 通にまとめて全部 / 初回（A）と前に足した（B・再接続）を分ける / 重複を作らない・切断しない /
 *     projects_list が通るまで「つながった」と言わない / 「認証を受け付けました。再試行してください」が 2 回なら止めて文・時刻・段を言う。
 * ページの貼る文はどのアプリ（Claude・ChatGPT・Muse・コマンドを実行できる AI）にも同じ 1 つ = 「ChatGPT」の所を「あなた（この AI のアプリ）」にした。
 *   アプリごとに違う所は「貼る値」の行（Muse のクライアント ID・Preview は一覧に無い）と、コマンドを実行できる AI の 1 行で持つ。
 * founder の文から変えた所（事実に合わせるため）:
 *   - 最後のボタン: founder の文は「press Allow」。KUMODeck のページの初めての接続のボタンは「Connect」（ja「連携する」）、許可を足すときが
 *     「Allow」（「許可する」）= ダッシュボードの i18n mcpAuth.allow / allowAdd。両方を書く（以前の afterConnectLine と同じ）
 *   - 「ChatGPT finds the settings by itself」→「your app finds…」・ドキュメントの URL は呼ぶ側の設定から（docsUrl・無ければ括弧の後ろを出さない）
 *   - アプリ自身の確認の画面（2026-10-25 console/connect-consent・Muse の「チャットとメモリを共有する接続を許可しますか？」）は
 *     「私が押す」と A に書き、「アプリの画面で、KUMODeck の画面ではない」と添える（founder が「kumo 側がストップしてるの？」と迷った件）
 * 以前あって外した物（founder の文が正）: 「1 歩ずつ教えて」「Do not ask me questions first」・画面を操作できる AI は手順を全部自分で（screenLine・
 *   2026-10-17 console/ai-does-connect）→ 拡張機能や画面の操作を求めない、設定は人がする、に変わった
 *   o.name     … 名前の欄に入れる名前（Preview は「KUMODeck Preview (v0.2)」・いつもは製品名）
 *   o.mcpUrl   … 接続の URL（無ければ値の行を出さない）
 *   o.docsUrl  … 困ったときに読むページ（.md の絶対 URL）。無ければ出さない（ドメインを推測で書かない）
 *   o.clientId … 「クライアント ID」の行（clientIdLine）。null = 出さない
 *   o.notInList … true = 一覧に無い行（Preview・2026-10-25 console/connect-consent の Muse の件）
 *   o.commandLine … コマンドを実行できる AI への 1 行（そのまま入れる）
 */
function checklistPrompt(o, ja) {
  const { p, name, mcpUrl, docsUrl } = o;
  const intro = ja
    ? [
        `${name}をあなた（この AI のアプリ）につないで、私のためにゲームを作って公開できるようにするのを手伝ってください。`,
        `ブラウザの拡張機能を入れることや、画面の操作（コンピューターの操作）をオンにすることは求めないでください。設定は私が自分でします。`,
        `手順は今、1 通にまとめて全部ください。1 回押すたびに止まって、何が見えるかを聞かないでください。私は手順を最後までやってから「できた」と言いに戻ります。うまくいかないときは、何が起きたかを伝えます。`
      ]
    : [
        `Help me connect ${name} to you (this AI app) so you can build and publish games for me.`,
        `Do not ask me to install a browser extension or turn on computer control. I will do the settings myself.`,
        `Give me the whole checklist in one message now. Do not stop after each click to ask what I see. I will come back and say "done" after completing the checklist, or describe the problem if something goes wrong.`
      ];
  const values = [ja ? '貼る値:' : 'Values to paste:', `- ${ja ? '名前' : 'Name'}: ${name}`];
  if (mcpUrl) values.push(`- URL: ${mcpUrl}`);
  values.push(
    ja
      ? `- 認証: OAuth（設定はアプリが自分で見つけます。クライアント ID やシークレットを求められても推測で入れないでください${docsUrl ? `。困ったら ${docsUrl} を読んでください` : ''}）`
      : `- Authentication: OAuth (your app finds the settings by itself; if it asks for a client ID or secret, do not guess${docsUrl ? ` — see ${docsUrl}` : ''})`
  );
  if (o.clientId) values.push(`- ${o.clientId}`);
  if (o.notInList) {
    values.push(
      ja
        ? `- アプリの一覧（Search plugins・ディレクトリ・Discover）にはありません。探さずに、URL で足してください。`
        : `- It is not in any app's list (Search plugins, directory or Discover): add it by URL, do not search for it.`
    );
  }
  const checklists = ja
    ? [
        `短い手順を 2 つ書いてください:`,
        `A) 初めて: コネクター（プラグイン）の設定をどこで開くか（設定画面の直接のリンクは、正しいと確かなときだけ）、カスタムの MCP サーバーを足す、上の値を貼る、${p}にサインイン、「作って公開する（おすすめ）」のまま「連携する」（または「許可する」）を押す。あなたのアプリ自身の確認の画面（会話やメモリの共有など）が出たら、その画面の Connect / Allow（接続・許可）は私が押します。その画面はあなたのアプリのもので、${p}のものではありません。`,
        `B) 前に足したことがある: 同じ設定で${name}を開いて Reconnect（再接続）を押す。2 つ目は足さない。切断しない。`
      ]
    : [
        `Write two short checklists:`,
        `A) First time: where to open the connector / plugin settings (give the direct settings link only if you are sure it is correct), add a custom MCP server, paste the values above, sign in to ${p}, keep "Build and publish (recommended)", press Connect (or Allow). If your app shows its own confirmation screen (such as sharing chats and memory), I press its Connect / Allow button. That screen is from your app, not from ${p}.`,
        `B) Already added before: open ${name} in the same settings and press Reconnect. Do not add a second one. Do not disconnect.`
      ];
  const check = ja
    ? `私が「できた」と戻ってきたら、projects_list（読むだけ）でつながったか確かめてください。projects_list が成功するまで「つながった」と言わないでください。この会話に道具が無ければ、新しい会話を始めて${name}を選び、そこで projects_list を試すように言ってください。`
    : `When I come back and say "done", check the connection with projects_list (read only). Do not say it works until projects_list succeeds. If the tools are not in this conversation, tell me to start a new conversation, select ${name} and try projects_list there.`;
  // 「認証を受け付けました」のくり返し（founder の文）: 何度も試させず、調べられる手がかり（文・時刻・段）を持って止める
  const stop = ja
    ? `「認証を受け付けました。再試行してください」（英語では "Authentication accepted. Please try again"）が 2 回出たら、止めて、出た文そのまま・時刻（タイムゾーンつき）・どの段かを私に伝えてください。何度も試し直さないでください。`
    : `If "Authentication accepted. Please try again" (or the same message in Japanese) appears twice, stop and tell me the exact message, the time (with time zone) and the step. Do not keep retrying.`;
  // 空行の位置も founder の文と同じ（前置き / 貼る値 / コマンド / 手順 / 確かめ / 止める + 締め）
  return [
    intro.join('\n'),
    values.join('\n'),
    ...(o.commandLine ? [o.commandLine] : []),
    checklists.join('\n'),
    check,
    [stop, askLine(p, ja, o.ownGame === true)].join('\n')
  ].join('\n\n');
}

/**
 * どの AI にも貼る 1 つの文（いつもの接続）。形は checklistPrompt（founder 10/25 の文）。
 *   guideUrl … 案内のページ（.md）の絶対 URL。困ったときに読む所（「認証」の行）。無い（サイトの URL を知らないビルド）なら出さない
 *   mcpUrl   … この環境の MCP の URL
 *   command  … パソコンで動くエージェントが実行する connect の行（例 `npx <パッケージ> connect --api <API>`）
 *   ownGame  … 広告から来た人（任意・true のときだけ締めの頼みが「このゲームを公開して」になる。askLine の WHY）
 *   museClientId … 呼ぶ側が渡したときだけ Muse の ID の行（2026-10-24 console/muse-client-id・本番の公開の MCP に決まった ID が入るまでは出さない）
 * 3 つとも無いなら null（手がかり無しに AI が {product} を探すと別物に当たりうる = 文を出さない）。
 * 「一覧に無い」の行はこちらには入れない: いつもの接続は審査が通れば一覧に載る（Preview は載らない）
 */
export function oneConnectPrompt(input, lang) {
  const { productName: p, guideUrl, mcpUrl, command } = input;
  if (!guideUrl && !mcpUrl && !command) return null;
  const ja = lang === 'ja';
  const clientId = input.museClientId ? (ja ? `クライアント ID（アプリが求めたときだけ）: Muse は \`${input.museClientId}\`（シークレットなし）。` : `Client ID, only if the app asks: in Muse, \`${input.museClientId}\` (no client secret).`) : null;
  const commandLine = command
    ? ja
      ? `私のパソコンでコマンドを実行できる AI エージェントなら、手順を書く代わりに \`${command}\` を実行してください。`
      : `If you are an AI agent that can run commands on my computer, run \`${command}\` instead of writing the checklists.`
    : null;
  return checklistPrompt({ p, name: p, mcpUrl, docsUrl: guideUrl, clientId, commandLine, ownGame: input.ownGame }, ja);
}

/**
 * 締めの 1 行（つないだ直後に人が AI に頼む文）。oneConnectPrompt と previewConnectPrompt で共通。
 *   ownGame = 広告から来た人（ダッシュボードの adVisit.ts の印・2026-10-18 gtm/early-fixes・GTM の確認 early-review.md の直す案 2）。
 *     広告は「Your AI built the game … tell it "publish this game"」= その人はもうゲームを持っている。なのに「小さなゲームを新しく作って」と
 *     頼ませると、価値（自分のゲームが URL で動く）の手前で寄り道になる → 1 つ目を「このゲームを公開して」、まだ無い人向けに今までの頼みを後ろに。
 *   印が無い人（招待のリンク・動画の概要欄・ふつうの登録）は今までの「小さなゲームを作って公開して」のまま。
 * 頼みの文はダッシュボードのホームの最初の頼み（en.home.ts home.askExample.first / firstAd）と同じにそろえる（AI が言う文と画面の文が同じ = 迷わない）
 * 頭は「projects_list が成功したら」（2026-10-25 console/connect-checklist・founder の文。以前は「つながったら」= 確かめる前に言えた）
 */
function askLine(p, ja, ownGame) {
  if (ownGame) {
    return ja
      ? `projects_list が成功したら「このゲームを${p}で公開して。遊べるURLを送って」と頼むように教えてください。まだゲームが無ければ「${p}で小さなゲームを作って公開して」。`
      : `When projects_list succeeds, tell me to ask: "Publish this game with ${p}. Send me the URL so I can play it." If I have no game yet: "Make a small game with ${p} and publish it."`;
  }
  return ja ? `projects_list が成功したら「${p}で小さなゲームを作って公開して」と頼むように教えてください。` : `When projects_list succeeds, tell me to ask: "Make a small game with ${p} and publish it."`;
}

/**
 * Preview（検証用の MCP・KUMODeck Preview (v0.2)）につなぐ貼る文（2026-10-16 console/guide-preview・founder の決定（Growth 経由））。
 * ダッシュボードの How to use の「AIをつなぐ」で、Preview が開いている（/v1/platform/info の previewMcpUrl がある）ときだけ、oneConnectPrompt の代わりに出す。
 * サイトの Preview のビルド（SITE_PREVIEW_MCP_URL）の手順のページの貼る文もこれ。founder が 10/25 に ChatGPT で試して OK した文はこの文の形。
 *   previewName … 「KUMODeck Preview (v0.2)」（ダッシュボードの productName.ts PREVIEW_NAME）
 *   mcpUrl      … Preview の MCP の URL（previewMcpUrl を mcpEndpoint で検査済み）。無いなら null
 *   serverId    … コマンドで足すときの短い名前（空白なし。例 kumodeck-preview）
 *   stepsUrl    … 手順のページ（/docs/claude-and-chatgpt/ の AI 向けの .md・webStepsPath）の絶対 URL。困ったときに読む所。無いなら出さない
 *     （2026-10-17 console/ai-does-connect: リンクが無いと ChatGPT は自分の知識で「どこを押すか」を答え、外れることがあった）
 * 入れないもの（founder 10/16・管理の指示）: 案内のページ（/docs/connect/）と connect のコマンド（`npx … connect`）。
 *   どちらも「いつもの接続」（公開の MCP）につながる = Preview を試したい人がいつもの方につないでしまう。
 *   代わりに、コマンドが使える AI には自分で足す短い例（Claude Code・Codex）。
 * 一覧には無い（2026-10-25 console/connect-consent・founder 10/25）: Muse は最初「上の Search plugins に KUMODeck Preview と入れて」と案内して
 *   行き止まりになった。アプリの一覧に載るのは審査中の本番だけ → notInList
 * Muse のクライアント ID: Preview（検証用の MCP）には決まった Muse の ID が先に入る（出す順: 検証用の MCP → サイト → 本番は固定の後）= いつも出す
 */
export function previewConnectPrompt(input, lang) {
  const { productName: p, previewName: name, mcpUrl, serverId, stepsUrl } = input;
  if (!mcpUrl) return null;
  const ja = lang === 'ja';
  const commandLine = ja
    ? `私のパソコンでコマンドを実行できる AI エージェントなら、手順を書く代わりに自分で足してください（例: Claude Code は \`claude mcp add --transport http ${serverId} ${mcpUrl}\`、Codex は \`codex mcp add ${serverId} --url ${mcpUrl}\`）。`
    : `If you are an AI agent that can run commands on my computer, add it yourself instead of writing the checklists (for example, Claude Code: \`claude mcp add --transport http ${serverId} ${mcpUrl}\`, Codex: \`codex mcp add ${serverId} --url ${mcpUrl}\`).`;
  return checklistPrompt({ p, name, mcpUrl, docsUrl: stepsUrl, clientId: clientIdLine(ja, MUSE_CLIENT_ID), notInList: true, commandLine, ownGame: input.ownGame }, ja);
}

/**
 * 案内のページの「Web・スマホの Claude / ChatGPT」の節の中身（Markdown。見出しは本文 connect.md の側）。AI に向けて書く（「ユーザー」= 貼った人）。
 * site は { productName, mcpUrl, dashboardUrl, connectName } だけを読む。
 * connectName（2026-10-16 site/all-preview）= 名前の欄に入れる名前・会話でオンにする名前。Preview のビルドは「KUMODeck Preview (v0.2)」、
 *   無ければ productName（以前と同じ文）。サインインのページ・アカウントは製品名のまま（許可の画面は KUMODeck の画面）
 */
export function webGuide(site, lang) {
  const p = site.productName;
  const n = site.connectName || p;
  const mcp = site.mcpUrl;
  const dash = site.dashboardUrl;
  if (lang === 'ja') {
    return `あなたはユーザーのパソコンでコマンドを実行できません。${n}をカスタムのコネクタとして足す手順を、ユーザーに渡してください。ほとんどの人は初めての設定です。

案内の決まり:

- 何も質問せず、すぐ手順を書く（あなたが Claude か ChatGPT かは、あなた自身が分かっている）。
- ブラウザの拡張機能を入れることや、画面の操作（コンピューターの操作）をオンにすることは求めない。設定はユーザーが自分でする。
- 手順は 1 通にまとめて全部渡す。1 回押すたびに止まって、何が見えるかを聞かない。ユーザーは最後までやってから「できた」と戻るか、困ったことを伝えに戻る。
- 短い手順を 2 つ書く。A) 初めて: 設定を開く → カスタムのコネクタを足す → 下の値を貼る → ${p}にサインイン → 「Build and publish (recommended)」のまま Connect（または Allow）。B) 前に足したことがある: 同じ設定で${n}を開いて Reconnect（再接続）。2 つ目は足さない。切断しない。
- あなたのアプリ自身の確認の画面（接続の許可・会話やメモリの共有など）が出たら、その画面のボタンはユーザーが押す、と A に書く。止めているのは${p}ではなく、あなたのアプリの決まりだと一言添える。
- 最初に一度だけ「この会話は開いたままにして、リンクは新しいタブで開く（Mac は ⌘、Windows は Ctrl を押しながらクリック）」と書く。
- 設定画面の直接のリンクは、今の正しい URL だと確かなときだけ、https:// から始まる押せるリンクで出す。
- ユーザーが「できた」と戻ったら projects_list（読むだけ）で確かめる。成功するまで「つながった」と言わない。この会話に道具が無ければ、新しい会話を始めて${n}を選び、そこで試すように伝える。
- 「認証を受け付けました。再試行してください」が 2 回出たら止めて、出た文そのまま・時刻（タイムゾーンつき）・どの段かをユーザーに伝えてもらう。何度も試し直させない。
- プランの制限・管理者・開発者モードの話は、ユーザーの画面が説明と違うときだけ出す。

使う事実:

- 接続先のURL: ${mcp}
- ${p}のアカウント（サインインを求められたらここ）: ${dash}
- Claude: Customize → Connectors → + Add → Add custom connector。名前は ${n}、Remote MCP server URL に上のURL → Add。開いた${p}のページでログインして Allow。会話では入力欄の「+」→ Connectors で ${n} をオン。
- ChatGPT: Plugins → + → Add custom MCP server。名前は ${n}、Server URL に上のURL、Authentication は OAuth、「I understand and want to continue」に印 → Create as a plugin。「+」でインストールし、${p}のページでログインして Allow。会話では @ を打って ${n} を選ぶ。
- 画面が違うときだけ: Claude の Free はカスタムのコネクタが1つまで（ほかを外す）。ChatGPT でボタンが無ければ Settings → Apps → Advanced settings の Developer mode。会社・学校のアカウントは管理者の許可が要る。スマホのアプリでは足せない（パソコンのブラウザで足せば、スマホでも使える）。`;
  }
  return `You cannot run commands on the user's computer. Give the user the steps to add ${n} as a custom connector. For most people this is the first time.

How to guide:

- Ask the user nothing; write the steps right away (you already know whether you are Claude or ChatGPT).
- Do not ask the user to install a browser extension or turn on computer control. The user does the settings.
- Give the whole checklist in one message. Do not stop after each click to ask what the user sees. The user comes back and says "done" after the checklist, or describes the problem.
- Write two short checklists. A) First time: open the settings → add a custom connector → paste the values below → sign in to ${p} → keep "Build and publish (recommended)" and press Connect (or Allow). B) Already added before: open ${n} in the same settings and press Reconnect. Do not add a second one. Do not disconnect.
- If your own app shows a confirmation screen (such as allowing the connection or sharing chats and memory), write in A that the user presses its button. Add that your app stops there, not ${p}.
- Once, at the start, tell the user to keep this conversation open and open every link in a new tab (Cmd-click on a Mac, Ctrl-click on Windows).
- Give the direct link to a settings page only if you are sure it is the current correct URL, as a full clickable link starting with https://.
- When the user says "done", check with projects_list (read only). Do not say it works until projects_list succeeds. If the tools are not in this conversation, tell the user to start a new conversation, select ${n} and try there.
- If "Authentication accepted. Please try again" appears twice, the user stops and tells you the exact message, the time (with time zone) and the step. Do not make them keep retrying.
- Do not mention plan limits, admins or developer mode unless the user's screen does not match.

Facts:

- Connection URL: ${mcp}
- ${p} account (sign in here if asked): ${dash}
- Claude: Customize → Connectors → + Add → Add custom connector. Name: ${n}. Remote MCP server URL: the URL above → Add. Sign in on the ${p} page that opens and press Allow. In a conversation: + → Connectors → turn on ${n}.
- ChatGPT: Plugins → + → Add custom MCP server. Name: ${n}. Server URL: the URL above. Authentication: OAuth. Tick "I understand and want to continue" → Create as a plugin. Press + to install it, sign in on the ${p} page and press Allow. In a conversation: type @ and pick ${n}.
- Only if the user's screen does not match: Claude Free allows one custom connector (remove the other one). If ChatGPT has no such button, try Developer mode in Settings → Apps → Advanced settings. Work and school accounts need their admin. The phone apps cannot add it (add it in a computer browser; then it works on the phone too).`;
}

/**
 * 案内のページの「C. カスタムコネクター（OAuth）を足せるほかの AI エージェント」の節の中身（Markdown。見出しは本文 connect.md の側・2026-10-24 console/muse-client-id）。
 * AI に向けて書く（「ユーザー」= 貼った人）。site は { productName, mcpUrl, connectName, mcpServerId, museClientId } を読む。
 * WHY（founder 10/24・Android の Muse で Preview をつないだときのフィードバック全文）:
 *   1. 案内は「Web の Claude / ChatGPT」と「コマンドを動かせるエージェント」の 2 つだけで、ホスト型の OAuth コネクターを持つエージェントの道が無かった
 *   2. クライアント ID が必須の画面で、どこで ID を取るか書いていなかった → 公式の見つけ方（PRM → 認可サーバーのメタデータ）と DCR の決まりを書く
 *   3・4. 「接続しました」の後も、何が終わったか・その会話で道具が使えるかが分からなかった → 最後のボタン・確かめの読み取り・新しい会話を書く
 *   5. 名前が 3 種類（表示名・サーバー名・アンダースコアの登録名）→ 対応表
 *   6.（2026-10-25 console/connect-consent・founder 10/25）Muse が一覧（Search plugins）で Preview を探させて行き止まり・Muse 自身の共有の確認で
 *      AI が Connect を押せず「許可いただければ進めます」と質問で終わった → 一覧で探さない・押すボタンを 1 行で言って続ける（2026-10-25 console/connect-checklist からは「押すのは人・アプリの画面で KUMODeck の画面ではない」と手順に書く）
 * 登録（DCR）の決まりは apps/mcp の provider の値: PKCE は S256 だけ・戻り先は完全一致・使われない登録は 90 日で消える（cleanup.ts の client:* の WHY）
 */
export function hostedGuide(site, lang) {
  const p = site.productName;
  const n = site.connectName || p;
  const mcp = site.mcpUrl;
  const origin = new URL(mcp).origin;
  const id = site.museClientId;
  const toolName = n.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (lang === 'ja') {
    return `あなたのアプリが MCP サーバーを「カスタムコネクター」として OAuth で足せて（例: Meta の Muse）、ユーザーのパソコンでコマンドは実行できない場合の道です。ユーザーに質問はせず、下の手順を 1 通にまとめて全部渡してください。ブラウザの拡張機能や画面の操作は求めない（設定はユーザーがする）。1 回押すたびに止まって何が見えるかを聞かない。

- アプリの一覧（Search plugins・ディレクトリ）で${n}を探さない。下の接続の URL でカスタムコネクターとして足す。
- あなたのアプリ自身の確認（Muse なら、関連する会話と、有効ならメモリを${p}と共有する接続を許可するかの確認）は、ユーザーが押す。手順に「その画面の〇〇ボタンを押す」と書き、質問の形にしない。止めているのは${p}ではなく、あなたのアプリの決まりだと一言添える。

使う事実:

- 接続の URL（コネクターの URL の欄に入れる）: ${mcp}
- 名前: ${n}
- クライアント ID: ${id ? `Muse は \`${id}\`（Muse 用に前もって登録したもの・シークレットなし。戻り先は Muse の受け口だけ）。ほかのアプリで ID を求められたら、下の「自分で登録する」で作った ID を使う。` : `下の「自分で登録する」で作った ID を使う。`}
- クライアントシークレット: 無し（公開クライアント・PKCE）

自分で登録する（クライアント ID を求めるアプリで、決まった ID が無いとき。動的クライアント登録 = DCR。あなたが自分で登録してよい）:

1. \`${origin}/.well-known/oauth-protected-resource\` を読む → \`authorization_servers\` の先の \`/.well-known/oauth-authorization-server\` を読む → \`authorization_endpoint\`・\`token_endpoint\`・\`registration_endpoint\` が分かる。
2. \`registration_endpoint\` に POST（JSON）: \`client_name\`（あなたのアプリの名前）、\`redirect_uris\`（あなたのアプリの OAuth の戻り先。/authorize に送るものと 1 文字も違わない値）、\`token_endpoint_auth_method\`: \`"none"\`、\`grant_types\`: \`["authorization_code", "refresh_token"]\`、\`response_types\`: \`["code"]\`。
3. 返ってきた \`client_id\` を、コネクターの画面のクライアント ID の欄に入れる（ユーザーに渡すなら、それだけを 1 行で）。PKCE は S256 が必須。使われない登録は 90 日で消える（そのときは登録し直す）。

ユーザーの手順（1 通にまとめて渡す。ユーザーは最後までやってから「できた」と戻る）:

A) 初めて:
1. コネクターを足す画面（Muse は「カスタムコネクターを追加」）で、URL に上の接続の URL、クライアント ID に上の ID を入れて **追加** を押す。
2. ${p}のページが開く。サインインして、選ぶ所は「**作って公開する（おすすめ）**」のまま **連携する**（足りない許可を足すときは **許可する**）を押す。**これがユーザーの押す最後のボタン**（アプリ自身の確認が出たら、そのボタンも）。

B) 前に足したことがある: 同じ設定で${n}を開いて再接続する。2 つ目は足さない。切断しない。

つながったか確かめる:

- ユーザーが「できた」と戻ったら、\`projects_list\`（プロジェクトの一覧を読むだけ・何も変えない）を 1 回呼ぶ。答えが返れば接続は完了。成功するまで「つながった」と言わない。
- この会話で${p}の道具が出ない・「資格情報がまだ無い」と出る場合: 多くのアプリは、道具と資格情報を会話の始まりに読み込む。そのときは「つながりました。新しい会話を始めて、そこで頼んでください」と 1 行で伝える。同じ会話で何度も試さない。
- 「認証を受け付けました。再試行してください」が 2 回出たら止めて、出た文そのまま・時刻（タイムゾーンつき）・どの段かをユーザーに伝えてもらう。

名前の対応（どれも同じ接続）:

| どこで | 名前 |
|---|---|
| コネクターの一覧・名前の欄 | ${n} |
| コマンド・設定ファイルのサーバー名 | ${site.mcpServerId} |
| アプリが道具の名前の頭に付ける名前（例） | ${toolName}（空白や記号が _ になる） |
| サインインと許可のページ | ${p} |`;
  }
  return `This is the way for an AI agent whose app adds MCP servers as "custom connectors" with OAuth (for example, Muse by Meta) and that cannot run commands on the user's computer. Ask the user nothing; give the steps below in one message. Do not ask the user to install a browser extension or turn on computer control (the user does the settings), and do not stop after each click to ask what they see.

- Do not search for ${n} in the app's list (Search plugins or directory). Add it as a custom connector with the connection URL below.
- If your own app shows a confirmation you are not allowed to press (in Muse: allowing a connection that shares related chats, and memory if it is on, with ${p}), the user presses it. Write it in the steps ("press [button] on that screen"), not as a question. Add that your app stops there, not ${p}.

Facts:

- Connection URL (put it in the connector's URL field): ${mcp}
- Name: ${n}
- Client ID: ${id ? `in Muse, \`${id}\` (registered for Muse in advance, no client secret, it returns only to Muse). If another app asks for a client ID, use the one you make under "Register yourself" below.` : `use the one you make under "Register yourself" below.`}
- Client secret: none (public client with PKCE)

Register yourself (when the app asks for a client ID and there is no fixed one; OAuth dynamic client registration, which you may do yourself):

1. Read \`${origin}/.well-known/oauth-protected-resource\` → read \`/.well-known/oauth-authorization-server\` on the server in \`authorization_servers\` → it lists \`authorization_endpoint\`, \`token_endpoint\` and \`registration_endpoint\`.
2. POST JSON to \`registration_endpoint\`: \`client_name\` (your app's name), \`redirect_uris\` (your app's OAuth callback, exactly the value it sends to /authorize), \`token_endpoint_auth_method\`: \`"none"\`, \`grant_types\`: \`["authorization_code", "refresh_token"]\`, \`response_types\`: \`["code"]\`.
3. Put the returned \`client_id\` in the connector's client ID field (if you give it to the user, give only that, on one line). PKCE with S256 is required. A registration that is not used for 90 days is removed (then register again).

Steps for the user (all in one message; the user comes back and says "done" after them):

A) First time:
1. On the screen that adds a connector (in Muse: "Add custom connector"), put the connection URL above in the URL field and the client ID above in the client ID field, and press **Add**.
2. The ${p} page opens. The user signs in, keeps "**Build and publish (recommended)**" and presses **Connect** (**Allow** when adding permissions). **That is the last button the user presses** (plus your app's own confirmation, if it shows one).

B) Already added before: open ${n} in the same settings and reconnect it. Do not add a second one. Do not disconnect.

Check that it is connected:

- When the user says "done", call \`projects_list\` once (it only reads the list of projects and changes nothing). If it answers, the connection is done. Do not say it works before that.
- If the ${p} tools do not appear in this conversation, or the app says there are no credentials yet: many apps load tools and credentials when a conversation starts. Tell the user in one line: "It is connected. Start a new conversation and ask there." Do not keep trying in the same conversation.
- If "Authentication accepted. Please try again" appears twice, the user stops and tells you the exact message, the time (with time zone) and the step.

Names (all the same connection):

| Where | Name |
|---|---|
| The list of connectors, the name field | ${n} |
| Server name in commands and config files | ${site.mcpServerId} |
| The prefix some apps put on tool names (example) | ${toolName} (spaces and marks become _) |
| The sign-in and permission page | ${p} |`;
}

/**
 * 手順のページ（claude-and-chatgpt）の Muse の節の「クライアント ID」の欄（Markdown・人が読む・2026-10-24 console/muse-client-id）。
 * ID がある（site.museClientId）ビルドはコピーできる欄（コードの枠 = Copy のボタン）、無いビルドは「Muse に自分で登録してもらう」の 1 行。
 * 「鍵ではない」を 1 行で書く WHY: ID の欄は見た目がパスワードの欄に近く、貼ってよいのか迷う
 */
export function museClientIdField(site, lang) {
  const p = site.productName;
  const id = site.museClientId;
  if (lang === 'ja') {
    return id
      ? `\`\`\`text クライアント ID\n${id}\n\`\`\`\n\nシークレットはありません。この ID はパスワードではなく、「Muse からの接続」という名前です（サインインの後に戻る先は Muse だけ）。`
      : `**クライアント ID:** Muse に「${p}に自分で登録して、その ID を使って」と頼みます（上の貼る文にも書いてあります）。シークレットはありません。`;
  }
  return id
    ? `\`\`\`text Client ID\n${id}\n\`\`\`\n\nThere is no client secret. This ID is not a password: it names "a connection from Muse" (after sign-in, it returns only to Muse).`
    : `**Client ID:** ask Muse to register itself with ${p} and use that ID (the text above says so too). There is no client secret.`;
}
